"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.StableSwap = void 0;
const currency_1 = require("@zenlink-interface/currency");
const math_1 = require("@zenlink-interface/math");
const tiny_invariant_1 = __importDefault(require("tiny-invariant"));
const errors_1 = require("../errors");
const FEE_DENOMINATOR = math_1.JSBI.BigInt(1e10);
const A_PRECISION = math_1.JSBI.BigInt(100);
const POOL_TOKEN_COMMON_DECIMALS = math_1.JSBI.BigInt(18);
class StableSwap {
    constructor(chainId, contractAddress, pooledTokens, liquidityToken, totalSupply, balances, swapFee, adminFee, A, virtualPrice) {
        this.chainId = chainId;
        this.contractAddress = contractAddress;
        this.pooledTokens = pooledTokens;
        this.liquidityToken = liquidityToken;
        this.totalSupply = totalSupply;
        this.tokenMultipliers = this.pooledTokens.map(token => math_1.JSBI.exponentiate(math_1.TEN, math_1.JSBI.subtract(POOL_TOKEN_COMMON_DECIMALS, math_1.JSBI.BigInt(token.decimals))));
        this.balances = balances;
        this.swapFee = swapFee;
        this.adminFee = adminFee;
        this.A = A;
        this.APrecise = math_1.JSBI.multiply(A, A_PRECISION);
        this.virtualPriceRaw = virtualPrice;
        this.virtualPrice = new math_1.Fraction(virtualPrice, math_1.JSBI.exponentiate(math_1.TEN, POOL_TOKEN_COMMON_DECIMALS));
    }
    _xp(balances, tokenMultipliers) {
        return balances.map((balance, i) => math_1.JSBI.multiply(balance.quotient, tokenMultipliers[i]));
    }
    _sumOf(x) {
        return x.reduce((memo, current) => math_1.JSBI.add(memo, current), math_1.ZERO);
    }
    _distance(x, y) {
        return math_1.JSBI.greaterThan(x, y) ? math_1.JSBI.subtract(x, y) : math_1.JSBI.subtract(y, x);
    }
    _getD(xp, amp) {
        const nCoins = math_1.JSBI.BigInt(xp.length);
        const sum = this._sumOf(xp);
        if (math_1.JSBI.equal(sum, math_1.ZERO))
            return math_1.ZERO;
        let Dprev = math_1.ZERO;
        let D = sum;
        const Ann = math_1.JSBI.multiply(amp, nCoins);
        for (let i = 0; i < 255; i++) {
            let D_P = D;
            for (let j = 0; j < xp.length; j++)
                D_P = math_1.JSBI.divide(math_1.JSBI.multiply(D_P, D), math_1.JSBI.multiply(xp[j], nCoins));
            Dprev = D;
            D = math_1.JSBI.divide(math_1.JSBI.multiply(math_1.JSBI.add(math_1.JSBI.divide(math_1.JSBI.multiply(Ann, sum), A_PRECISION), math_1.JSBI.multiply(D_P, nCoins)), D), math_1.JSBI.add(math_1.JSBI.divide(math_1.JSBI.multiply(math_1.JSBI.subtract(Ann, A_PRECISION), D), A_PRECISION), math_1.JSBI.multiply(math_1.JSBI.add(nCoins, math_1.ONE), D_P)));
            if (math_1.JSBI.lessThanOrEqual(this._distance(D, Dprev), math_1.ONE))
                return D;
        }
        throw new errors_1.CalculationError();
    }
    _getY(inIndex, outIndex, inBalance, normalizedBalances) {
        (0, tiny_invariant_1.default)(inIndex !== outIndex, 'sameToken');
        const nCoins = this.pooledTokens.length;
        (0, tiny_invariant_1.default)(inIndex < nCoins && outIndex < nCoins, 'indexOutOfRange');
        const amp = this.APrecise;
        const Ann = math_1.JSBI.multiply(amp, math_1.JSBI.BigInt(nCoins));
        const D = this._getD(normalizedBalances, amp);
        let sum = math_1.ZERO;
        let c = D;
        for (let i = 0; i < nCoins; i++) {
            if (i === outIndex)
                continue;
            const x = i === inIndex ? inBalance : normalizedBalances[i];
            sum = math_1.JSBI.add(sum, x);
            c = math_1.JSBI.divide(math_1.JSBI.multiply(c, D), math_1.JSBI.multiply(x, math_1.JSBI.BigInt(nCoins)));
        }
        c = math_1.JSBI.divide(math_1.JSBI.multiply(math_1.JSBI.multiply(c, D), A_PRECISION), math_1.JSBI.multiply(Ann, math_1.JSBI.BigInt(nCoins)));
        const b = math_1.JSBI.add(sum, math_1.JSBI.divide(math_1.JSBI.multiply(D, A_PRECISION), Ann));
        let lastY = math_1.ZERO;
        let y = D;
        for (let i = 0; i < 255; i++) {
            lastY = y;
            y = math_1.JSBI.divide(math_1.JSBI.add(math_1.JSBI.multiply(y, y), c), math_1.JSBI.subtract(math_1.JSBI.add(math_1.JSBI.multiply(math_1.TWO, y), b), D));
            if (math_1.JSBI.lessThanOrEqual(this._distance(lastY, y), math_1.ONE))
                return y;
        }
        throw new errors_1.CalculationError();
    }
    _getYD(A, index, xp, D) {
        const nCoins = this.pooledTokens.length;
        (0, tiny_invariant_1.default)(index < nCoins, 'indexOutOfRange');
        const Ann = math_1.JSBI.multiply(A, math_1.JSBI.BigInt(nCoins));
        let c = D;
        let s = math_1.ZERO;
        let _x = math_1.ZERO;
        let yPrev = math_1.ZERO;
        for (let i = 0; i < nCoins; i++) {
            if (i === index)
                continue;
            _x = xp[i];
            s = math_1.JSBI.add(s, _x);
            c = math_1.JSBI.divide(math_1.JSBI.multiply(c, D), math_1.JSBI.multiply(_x, math_1.JSBI.BigInt(nCoins)));
        }
        c = math_1.JSBI.divide(math_1.JSBI.multiply(math_1.JSBI.multiply(c, D), A_PRECISION), math_1.JSBI.multiply(Ann, math_1.JSBI.BigInt(nCoins)));
        const b = math_1.JSBI.add(s, math_1.JSBI.divide(math_1.JSBI.multiply(D, A_PRECISION), Ann));
        let y = D;
        for (let i = 0; i < 255; i++) {
            yPrev = y;
            y = math_1.JSBI.divide(math_1.JSBI.add(math_1.JSBI.multiply(y, y), c), math_1.JSBI.subtract(math_1.JSBI.add(math_1.JSBI.multiply(math_1.TWO, y), b), D));
            if (math_1.JSBI.lessThanOrEqual(this._distance(yPrev, y), math_1.ONE))
                return y;
        }
        throw new errors_1.CalculationError();
    }
    get _feePerToken() {
        const nCoins = this.pooledTokens.length;
        return math_1.JSBI.divide(math_1.JSBI.multiply(this.swapFee, math_1.JSBI.BigInt(nCoins)), math_1.JSBI.multiply(math_1.FOUR, math_1.JSBI.subtract(math_1.JSBI.BigInt(nCoins), math_1.ONE)));
    }
    get xp() {
        return this._xp(this.balances, this.tokenMultipliers);
    }
    getTokenIndex(token) {
        return this.pooledTokens.findIndex(pooledToken => pooledToken.equals(token));
    }
    getToken(index) {
        return this.pooledTokens[index];
    }
    involvesToken(token) {
        return this.pooledTokens.some(pooledToken => pooledToken.equals(token));
    }
    calculateTokenAmount(amounts, deposit) {
        const nCoins = this.pooledTokens.length;
        (0, tiny_invariant_1.default)(amounts.length === nCoins, 'invalidAmountsLength');
        const amp = this.APrecise;
        const D0 = this._getD(this.xp, amp);
        const newBalances = this.balances.map((balance, i) => deposit ? balance.add(amounts[i]) : balance.subtract(amounts[i]));
        const D1 = this._getD(this._xp(newBalances, this.tokenMultipliers), amp);
        const totalSupply = this.totalSupply.quotient;
        if (math_1.JSBI.equal(totalSupply, math_1.ZERO))
            return currency_1.Amount.fromRawAmount(this.liquidityToken, D1);
        const diff = deposit ? math_1.JSBI.subtract(D1, D0) : math_1.JSBI.subtract(D0, D1);
        return currency_1.Amount.fromRawAmount(this.liquidityToken, math_1.JSBI.divide(math_1.JSBI.multiply(diff, this.totalSupply.quotient), D0));
    }
    calculateSwap(inIndex, outIndex, inAmount) {
        const normalizedBalances = this.xp;
        const newInBalance = math_1.JSBI.add(normalizedBalances[inIndex], math_1.JSBI.multiply(inAmount.quotient, this.tokenMultipliers[inIndex]));
        const outBalance = this._getY(inIndex, outIndex, newInBalance, normalizedBalances);
        const outAmount = math_1.JSBI.divide(math_1.JSBI.subtract(math_1.JSBI.subtract(normalizedBalances[outIndex], outBalance), math_1.ONE), this.tokenMultipliers[outIndex]);
        const fee = math_1.JSBI.divide(math_1.JSBI.multiply(this.swapFee, outAmount), FEE_DENOMINATOR);
        return currency_1.Amount.fromRawAmount(this.pooledTokens[outIndex], math_1.JSBI.subtract(outAmount, fee));
    }
    calculateRemoveLiquidity(amount) {
        (0, tiny_invariant_1.default)(math_1.JSBI.lessThanOrEqual(amount.quotient, this.totalSupply.quotient), 'Cannot exceed total supply');
        return this.balances.map((balance, i) => currency_1.Amount.fromRawAmount(this.pooledTokens[i], math_1.JSBI.divide(math_1.JSBI.multiply(balance.quotient, amount.quotient), this.totalSupply.quotient)));
    }
    calculateRemoveLiquidityOneToken(amount, index) {
        (0, tiny_invariant_1.default)(index < this.pooledTokens.length, 'indexOutOfRange');
        const amp = this.APrecise;
        const xp = this.xp;
        const D0 = this._getD(xp, amp);
        const D1 = math_1.JSBI.subtract(D0, math_1.JSBI.divide(math_1.JSBI.multiply(amount.quotient, D0), this.totalSupply.quotient));
        const newY = this._getYD(amp, index, xp, D1);
        const reducedXP = xp;
        const _fee = this._feePerToken;
        for (let i = 0; i < this.pooledTokens.length; i++) {
            let expectedDx = math_1.ZERO;
            if (i === index)
                expectedDx = math_1.JSBI.subtract(math_1.JSBI.divide(math_1.JSBI.multiply(xp[i], D1), D0), newY);
            else
                expectedDx = math_1.JSBI.subtract(xp[i], math_1.JSBI.divide(math_1.JSBI.multiply(xp[i], D1), D0));
            reducedXP[i] = math_1.JSBI.subtract(reducedXP[i], math_1.JSBI.divide(math_1.JSBI.multiply(_fee, expectedDx), FEE_DENOMINATOR));
        }
        let dy = math_1.JSBI.subtract(reducedXP[index], this._getYD(amp, index, reducedXP, D1));
        dy = math_1.JSBI.divide(math_1.JSBI.subtract(dy, math_1.ONE), this.tokenMultipliers[index]);
        const fee = math_1.JSBI.subtract(math_1.JSBI.divide(math_1.JSBI.subtract(xp[index], newY), this.tokenMultipliers[index]), dy);
        return [
            currency_1.Amount.fromRawAmount(this.pooledTokens[index], dy),
            currency_1.Amount.fromRawAmount(this.pooledTokens[index], fee),
        ];
    }
}
exports.StableSwap = StableSwap;
